import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { CourseIdParamSchema } from './course.schema.js';
import { UserIdParamSchema } from './user.schema.js';
extendZodWithOpenApi(z);
// Re-export canonical param schemas so consumers importing from enrolment still work
export { CourseIdParamSchema, UserIdParamSchema };
const uuidSchema = z.string().uuid();
export const EnrolmentSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Enrolment record ID',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    userId: uuidSchema.openapi({ description: 'The enrolled user ID' }),
    courseId: uuidSchema.openapi({ description: 'The course ID' }),
    status: z.enum(['active', 'revoked', 'expired', 'suspended']).openapi({
        description: 'Current enrolment status',
        example: 'active',
    }),
    source: z.enum(['admin', 'self', 'purchase', 'subscription']).openapi({
        description: 'How the enrolment was created',
        example: 'admin',
    }),
    enrolledAt: z.string().nullable().openapi({ description: 'When the user was enrolled' }),
    enrolledBy: uuidSchema.nullable().openapi({
        description: 'User ID of who created the enrolment (null for self-enrolment)',
    }),
    activatedAt: z.string().nullable().openapi({ description: 'When the enrolment became active' }),
    startedAt: z
        .string()
        .nullable()
        .openapi({ description: 'When the user first accessed the course' }),
    completedAt: z
        .string()
        .nullable()
        .openapi({ description: 'When the user completed the course' }),
    lastAccessedAt: z
        .string()
        .nullable()
        .openapi({ description: 'Most recent course access timestamp' }),
    endedAt: z
        .string()
        .nullable()
        .openapi({ description: 'When the enrolment ended (revoked/expired)' }),
    endedReason: z.string().nullable().openapi({
        description: 'Reason for enrolment ending',
        example: 'Admin revoked access',
    }),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
})
    .openapi('Enrolment');
// --- Param schemas ---
// CourseIdParamSchema and UserIdParamSchema are imported from their canonical
// source files (course.schema and user.schema) and re-exported above.
export const CourseEnrolmentParamSchema = z.object({
    courseId: uuidSchema.openapi({ description: 'Course UUID' }),
    userId: uuidSchema.openapi({ description: 'User UUID' }),
});
export const UserEnrolmentParamSchema = z.object({
    userId: uuidSchema.openapi({ description: 'User UUID' }),
    courseId: uuidSchema.openapi({ description: 'Course UUID' }),
});
// --- Body schemas ---
export const EnrolUserBodySchema = z
    .object({
    userId: uuidSchema.openapi({
        description: 'ID of the user to enrol',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
})
    .openapi('EnrolUserBody');
export const EnrolInCourseBodySchema = z
    .object({
    courseId: uuidSchema.openapi({
        description: 'ID of the course to enrol the user in',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
})
    .openapi('EnrolInCourseBody');
// --- Query schemas ---
export const EnrolmentListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1).openapi({
        description: 'Page number',
        example: 1,
    }),
    pageSize: z.coerce.number().int().min(1).max(100).default(20).openapi({
        description: 'Items per page (max 100)',
        example: 20,
    }),
    searchQuery: z.string().optional().openapi({
        description: 'Search by user name or email',
    }),
});
//# sourceMappingURL=enrolment.schema.js.map